import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { Controller, useFormContext } from "react-hook-form";

import { EditIcon } from "../../assets";
import { preferredContactMethod, PHONE_NUMBER_MASKS } from "../../constants";
import { getSelectOptions } from "../../shared/utility";
import CustomSelect from "../../shared/components/select/CustomSelect";
import RoundedButton from "../../shared/components/buttons/RoundedButton";
import CustomError from "../../shared/components/custom-error/CustomError";
import CurrencyDropdown from "../../layout/CurrencyDropdown";

const PersonalDetails = ({
  isAgent,
  isEditing,
  allLanguages,
  allCurrencies,
  defaultCurrency,
  onCurrencyChange,
  handleChangeEditor,
}) => {
  const [phoneLength, setPhoneLength] = useState({ agent: 0, emergency: 0 });

  const {
    watch,
    setValue,
    setError,
    register,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    if (!isEditing) {
      clearErrors("agent.phoneNumber");
    } else {
      if (
        phoneLength.agent > watch("agent.phoneNumber")?.length &&
        watch("agent.phoneNumber")?.length < 15
      ) {
        setError("agent.phoneNumber", {
          type: "minLength",
          message: "Invalid phone number",
        });
      } else {
        clearErrors("agent.phoneNumber");
      }
    }

    // eslint-disable-next-line
  }, [watch("agent.phoneNumber"), isEditing]);

  useEffect(() => {
    if (!isEditing) {
      clearErrors("agent.emergencyContactInfo.phoneNumber");
    } else {
      if (
        phoneLength.emergency >
          watch("agent.emergencyContactInfo.phoneNumber")?.length &&
        watch("agent.emergencyContactInfo.phoneNumber")?.length < 15
      ) {
        setError("agent.emergencyContactInfo.phoneNumber", {
          type: "minLength",
          message: "Invalid phone number",
        });
      } else {
        clearErrors("agent.emergencyContactInfo.phoneNumber");
        return;
      }
    }

    // eslint-disable-next-line
  }, [watch("agent.emergencyContactInfo.phoneNumber"), isEditing]);

  return (
    <div className="grey-bg-wrapper">
      <div className="d-flex justify-content-between">
        <div className="heading">Personal Details</div>
        {!isEditing && (
          <div
            className="edit-button pointer"
            onClick={() => handleChangeEditor(true)}
          >
            <img src={EditIcon} alt="edit-icon" />
            Edit
          </div>
        )}
      </div>
      {!isAgent && (
        <>
          <div className="heading sub-heading">Default Currency</div>
          <div className="input-field mt-3">
            <CurrencyDropdown
              allCurrencies={allCurrencies}
              isEditing={isEditing}
              onCurrencyChange={onCurrencyChange}
              selectedCurrency={defaultCurrency}
            />
          </div>
          <div className="heading sub-heading">First Name</div>
          <div className="input-field">
            <input type="text" disabled={!isEditing} {...register("name")} />
            <CustomError errorMessage={errors?.name?.message} />
          </div>
          <div className="heading sub-heading">Last Name</div>
          <div className="input-field">
            <input
              type="text"
              disabled={!isEditing}
              {...register("lastName")}
            />
            <CustomError errorMessage={errors?.lastName?.message} />
          </div>
        </>
      )}
      {isAgent && (
        <>
          <div className="heading sub-heading">Preferred Contact Method</div>
          <div className="input-field">
            <CustomSelect
              isEditing={isEditing}
              options={getSelectOptions(preferredContactMethod, "name", "id")}
              name="agent.preferredContactMethod"
              placeholder="Preferred contact method"
            />
          </div>
          <div className="heading sub-heading">First Name</div>
          <div className="input-field">
            <input
              type="text"
              disabled={!isEditing}
              {...register("agent.firstName")}
            />
            <CustomError errorMessage={errors?.agent?.firstName?.message} />
          </div>
          <div className="heading sub-heading">Last Name</div>
          <div className="input-field">
            <input
              type="text"
              disabled={!isEditing}
              {...register("agent.lastName")}
            />
            <CustomError errorMessage={errors?.agent?.lastName?.message} />
          </div>

          <div className="heading sub-heading">Phone Number:</div>
          <div className="input-field">
            <>
              <Controller
                name="agent.phoneNumber"
                placeHolder="(916) 687-9606"
                rules={{ required: true }}
                render={({ field: { ref, ...field } }) => (
                  <PhoneInput
                    disabled={!isEditing}
                    disableDropdown={!isEditing}
                    isValid
                    placeholder="(916) 687-9606"
                    {...field}
                    inputExtraProps={{
                      ref,
                      required: true,
                      autoFocus: true,
                    }}
                    onChange={(phone, country) => {
                      setValue("agent.phoneNumber", phone);
                      setPhoneLength((prev) => ({
                        ...prev,
                        agent: country.format
                          .split("")
                          .filter((char) => char === ".").length,
                      }));
                    }}
                    country={"us"} // Default country code
                    masks={PHONE_NUMBER_MASKS}
                  />
                )}
              />
              <CustomError errorMessage={errors?.agent?.phoneNumber?.message} />
            </>
          </div>
        </>
      )}
      <div className="heading sub-heading">Email</div>
      <div className="input-field">
        <input
          type="email"
          disabled={!isEditing}
          {...register(isAgent ? "agent.email" : "email")}
        />
        <CustomError
          errorMessage={errors?.agent?.email?.message || errors?.email?.message}
        />
      </div>
      {isAgent && (
        <>
          <div className="heading sub-heading">Languages Spoken</div>
          <div className="input-field">
            <CustomSelect
              isEditing={isEditing}
              options={getSelectOptions(allLanguages, "name", "id")}
              name="agent.languageSpokenIds"
              placeholder="Languages spoken"
              isMulti
            />
          </div>
          <div className="heading mt-5">Emergency Contact</div>

          <div className="heading sub-heading">Full Name</div>
          <div className="input-field">
            <input
              type="text"
              disabled={!isEditing}
              {...register("agent.emergencyContactInfo.fullName")}
            />
            <CustomError
              errorMessage={
                errors?.agent?.emergencyContactInfo?.fullName?.message
              }
            />
          </div>

          <div className="heading sub-heading">Phone Number</div>
          <div className="input-field">
            <>
              <Controller
                name="agent.emergencyContactInfo.phoneNumber"
                placeHolder="(916) 687-9606"
                rules={{ required: true }}
                render={({ field: { ref, ...field } }) => (
                  <PhoneInput
                    placeholder="(916) 687-9606"
                    disabled={!isEditing}
                    disableDropdown={!isEditing}
                    {...field}
                    inputExtraProps={{
                      ref,
                      required: true,
                      autoFocus: true,
                    }}
                    onChange={(phone, country) => {
                      setValue("agent.emergencyContactInfo.phoneNumber", phone);
                      setPhoneLength((prev) => ({
                        ...prev,
                        emergency: country.format
                          .split("")
                          .filter((char) => char === ".").length,
                      }));
                    }}
                    country={"us"} // Default country code
                    masks={PHONE_NUMBER_MASKS}
                  />
                )}
              />
              <CustomError
                errorMessage={
                  errors?.agent?.emergencyContactInfo?.phoneNumber?.message
                }
              />
            </>
          </div>
        </>
      )}
      {isEditing && (
        <div className="mt-4 d-flex justify-content-center gap-3">
          <RoundedButton className="px-4 px-md-5 py-1" type="submit">
            Update
          </RoundedButton>
          <RoundedButton
            className="px-4 px-md-5 py-1"
            variant="outlined"
            onClick={() => handleChangeEditor(false)}
          >
            Cancel
          </RoundedButton>
        </div>
      )}
    </div>
  );
};

export default PersonalDetails;
